import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { eventsService, Event } from '../../services/events.service';
import { infoItemsService, InfoItem, InfoCategory } from '../../services/info-items.service';
import { kaizenService, KaizenIdeaRow } from '../../services/kaizen.service';
import { getInitials } from '../../utils/helpers';
import { isCoAdminOrAdmin } from '../../utils/roles';
import './LeftSidebar.css';
import EventsList from './EventsList';

const LeftSidebar: React.FC = () => {
  const { user } = useAuth();

  // Events state
  const [events, setEvents] = useState<Event[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    eventDate: '',
    eventTime: '',
    description: '',
    displayOrder: 0,
  });
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [shouldNotify, setShouldNotify] = useState(false);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [eventError, setEventError] = useState('');
  const [showDeleteEventModal, setShowDeleteEventModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Info items state (Saved for You = LOCAL, Be the Right ONE = GLOBAL)
  const [infoItems, setInfoItems] = useState<InfoItem[]>([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [editingInfo, setEditingInfo] = useState<InfoItem | null>(null);
  const [infoFormData, setInfoFormData] = useState({
    category: 'GLOBAL' as InfoCategory,
    title: '',
    displayOrder: 0,
  });
  const [infoFile, setInfoFile] = useState<File | null>(null);
  const [isUploadingInfo, setIsUploadingInfo] = useState(false);
  const [infoError, setInfoError] = useState('');
  const [showDeleteInfoModal, setShowDeleteInfoModal] = useState(false);
  const [infoToDelete, setInfoToDelete] = useState<InfoItem | null>(null);
  const [isDeletingInfo, setIsDeletingInfo] = useState(false);

  const [showKaizenModal, setShowKaizenModal] = useState(false);
  const [kaizenForm, setKaizenForm] = useState({ title: '', idea: '', area: '' });
  const [kaizenSubmitting, setKaizenSubmitting] = useState(false);
  const [kaizenError, setKaizenError] = useState('');
  const [showKaizenListModal, setShowKaizenListModal] = useState(false);
  const [kaizenIdeas, setKaizenIdeas] = useState<KaizenIdeaRow[]>([]);
  const [kaizenListLoading, setKaizenListLoading] = useState(false);
  const [kaizenListError, setKaizenListError] = useState('');

  const [kaizenSuccess, setKaizenSuccess] = useState(false);

  const canEdit = isCoAdminOrAdmin(user?.role);

  useEffect(() => {
    const hasModalOpen =
      showEventModal ||
      showDeleteEventModal ||
      showInfoModal ||
      showDeleteInfoModal ||
      showKaizenModal ||
      showKaizenListModal;

    if (hasModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [
    showEventModal,
    showDeleteEventModal,
    showInfoModal,
    showDeleteInfoModal,
    showKaizenModal,
    showKaizenListModal,
  ]);

  const openKaizenModal = () => {
    setKaizenError('');
    setKaizenSuccess(false);  // ← add this
    setKaizenForm({
      title: '',
      idea: '',
      area: user?.branch?.trim() || '',
    });
    setShowKaizenModal(true);
  };

  const closeKaizenModal = () => {
    if (kaizenSubmitting) return;
    setShowKaizenModal(false);
    setKaizenSuccess(false);  // ← reset so next open starts fresh
  };

  const openKaizenListModal = () => {
    setKaizenListError('');
    setShowKaizenListModal(true);
    setKaizenListLoading(true);
    kaizenService
      .list()
      .then((rows) => setKaizenIdeas(rows))
      .catch((err: any) => {
        const m = err.response?.data?.message;
        const msg = Array.isArray(m) ? m.join(' ') : m;
        setKaizenListError(msg || err.message || 'Failed to load ideas.');
        setKaizenIdeas([]);
      })
      .finally(() => setKaizenListLoading(false));
  };

  const closeKaizenListModal = () => {
    setShowKaizenListModal(false);
  };

  useEffect(() => {
    const isAnyModalOpen =
      showEventModal ||
      showDeleteEventModal ||
      showInfoModal ||
      showDeleteInfoModal ||
      showKaizenModal ||
      showKaizenListModal;

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [
    showEventModal,
    showDeleteEventModal,
    showInfoModal,
    showDeleteInfoModal,
    showKaizenModal,
    showKaizenListModal,
  ]);

  const formatKaizenSubmittedAt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const handleKaizenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setKaizenError('');
    const idea = kaizenForm.idea.trim();
    if (!idea) {
      setKaizenError('Please describe your Kaizen idea.');
      return;
    }
    setKaizenSubmitting(true);
    try {
      await kaizenService.submit({
        idea,
        title: kaizenForm.title.trim() || undefined,
        area: kaizenForm.area.trim() || undefined,
      });
      setKaizenForm({ title: '', idea: '', area: user?.branch?.trim() || '' });
      // ← removed setShowKaizenModal(false) — keep modal open to show success screen
      setKaizenSuccess(true);
    } catch (err: any) {
      const m = err.response?.data?.message;
      const msg = Array.isArray(m) ? m.join(' ') : m;
      setKaizenError(msg || err.message || 'Failed to submit. Please try again.');
    } finally {
      setKaizenSubmitting(false);
    }
  };

  useEffect(() => {
    loadEvents();
    loadInfoItems();
  }, [user?.id]);

  useEffect(() => {
    const isAnyModalOpen =
      showEventModal ||
      showDeleteEventModal ||
      showInfoModal ||
      showDeleteInfoModal ||
      showKaizenModal ||
      showKaizenListModal;

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [
    showEventModal,
    showDeleteEventModal,
    showInfoModal,
    showDeleteInfoModal,
    showKaizenModal,
    showKaizenListModal,
  ]);

  // ── Events ───────────────────────────────────────────────────────────────

  const loadEvents = async () => {
    try {
      const data = await eventsService.getUpcoming();
      // Exclude only explicit “new employee” spotlight rows (not string/number truthy quirks)
      setEvents(
        data.filter((ev) => ev.isNewEmployee !== true && ev.isNewEmployee !== 1),
      );
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventFormData({ title: '', eventDate: '', eventTime: '', description: '', displayOrder: events.length });
    setEventError('');
    setShouldNotify(false);
    setShowEventModal(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      eventDate: event.eventDate,
      eventTime: event.eventTime || '',
      description: event.description || '',
      displayOrder: event.displayOrder,
    });
    setImage(null);
    setImagePreview(event.imageUrl || null);
    setShouldNotify(false);
    setEventError('');
    setShowEventModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEventError('');
    if (!eventFormData.title || !eventFormData.eventDate) {
      setEventError('Title and event date are required');
      return;
    }
    setIsSavingEvent(true);
    try {
      let imageUrl: string | undefined = undefined;
      if (image) {
        const fd = new FormData();
        fd.append('file', image);
        const res = await eventsService.uploadImage(fd);
        imageUrl = res.url;
      } else if (editingEvent?.imageUrl) {
        imageUrl = editingEvent.imageUrl;
      }
      const data: any = {
        title: eventFormData.title,
        eventDate: eventFormData.eventDate,
        eventTime: eventFormData.eventTime || undefined,
        description: eventFormData.description || undefined,
        displayOrder: eventFormData.displayOrder,
        shouldNotify,
      };
      if (imageUrl !== undefined) data.imageUrl = imageUrl;
      if (editingEvent) {
        await eventsService.update(editingEvent.id, data);
      } else {
        await eventsService.create(data);
      }
      setShowEventModal(false);
      setImage(null);
      setImagePreview(null);
      setShouldNotify(false);
      loadEvents();
    } catch (error: any) {
      setEventError(error.response?.data?.message || 'Failed to save event');
    } finally {
      setIsSavingEvent(false);
    }
  };

  const handleDeleteEvent = (id: string) => {
    setEventToDelete(id);
    setShowDeleteEventModal(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    setIsDeleting(true);
    try {
      await eventsService.delete(eventToDelete);
      setShowDeleteEventModal(false);
      setEventToDelete(null);
      loadEvents();
    } catch {
      alert('Failed to delete event.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteEvent = () => {
    setShowDeleteEventModal(false);
    setEventToDelete(null);
  };

  // ── Info Items ────────────────────────────────────────────────────────────

  const loadInfoItems = async () => {
    try {
      const data = await infoItemsService.getAll();
      setInfoItems(data);
    } catch (error) {
      console.error('Failed to load info items:', error);
    }
  };

  const handleCreateInfo = (category: InfoCategory) => {
    setEditingInfo(null);
    const catItems = infoItems.filter((i) => i.category === category);
    setInfoFormData({ category, title: '', displayOrder: catItems.length });
    setInfoFile(null);
    setInfoError('');
    setShowInfoModal(true);
  };

  const handleEditInfo = (item: InfoItem) => {
    setEditingInfo(item);
    setInfoFormData({ category: item.category, title: item.title, displayOrder: item.displayOrder });
    setInfoFile(null);
    setInfoError('');
    setShowInfoModal(true);
  };

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfoError('');
    if (!editingInfo && !infoFile) {
      setInfoError('Please select a document file');
      return;
    }
    setIsUploadingInfo(true);
    try {
      let documentUrl = editingInfo?.documentUrl || '';
      if (infoFile) {
        const res = await infoItemsService.uploadDocument(infoFile);
        documentUrl = res.url;
      }
      const data = { category: infoFormData.category, title: infoFormData.title, documentUrl, displayOrder: infoFormData.displayOrder };
      if (editingInfo) {
        await infoItemsService.update(editingInfo.id, data);
      } else {
        await infoItemsService.create(data);
      }
      setShowInfoModal(false);
      loadInfoItems();
    } catch (error: any) {
      setInfoError(error.response?.data?.message || 'Failed to save item');
    } finally {
      setIsUploadingInfo(false);
    }
  };

  const handleDeleteInfo = (item: InfoItem) => {
    setInfoToDelete(item);
    setShowDeleteInfoModal(true);
  };

  const confirmDeleteInfo = async () => {
    if (!infoToDelete) return;
    setIsDeletingInfo(true);
    try {
      await infoItemsService.delete(infoToDelete.id);
      setShowDeleteInfoModal(false);
      setInfoToDelete(null);
      loadInfoItems();
    } catch {
      alert('Failed to delete item.');
    } finally {
      setIsDeletingInfo(false);
    }
  };

  const cancelDeleteInfo = () => {
    setShowDeleteInfoModal(false);
    setInfoToDelete(null);
  };

  const globalItems = infoItems.filter((i) => i.category === 'GLOBAL');
  const localItems = infoItems.filter((i) => i.category === 'LOCAL');

  useEffect(() => {
    if (showInfoModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showInfoModal]);

  return (
    <aside className="left-sidebar">

      {/* ── Profile Card ── */}
      <div className="profile-card">
        <div className="profile-avatar">
          {user?.profilePicture ? (
            <img src={user.profilePicture} alt={user.fullName} />
          ) : (
            user ? getInitials(user.fullName) : 'U'
          )}
        </div>
        <h3 className="profile-name">{user?.fullName}</h3>
        <span className="profile-role">
          {user?.role === 'CO_ADMIN'
            ? 'Co-Admin'
            : user?.role === 'CONTENT_UPLOADER'
              ? 'Content uploader'
              : (user?.role ?? 'EMPLOYEE').replace(/_/g, ' ')}
        </span>
      </div>

      {/* ── Kaizen box (below profile) ── */}
      <div className="ls-kaizen-block">
        <button
          type="button"
          className="ls-kaizen-strip"
          onClick={openKaizenModal}
          aria-label="Kaizen box — submit a continuous improvement idea"
        >
          <span className="ls-kaizen-strip-bg" aria-hidden />
          <span className="ls-kaizen-strip-overlay" aria-hidden />
          <span className="ls-kaizen-strip-content">
            <span className="ls-kaizen-strip-title">Kaizen box</span>
            <span className="ls-kaizen-strip-sub">Share an improvement idea</span>
          </span>
          <span className="ls-kaizen-strip-chevron" aria-hidden>
            →
          </span>
        </button>
        {canEdit && (
          <button
            type="button"
            className="ls-kaizen-view-link"
            onClick={openKaizenListModal}
            aria-label="View submitted Kaizen ideas (admin)"
          >
            View submitted ideas
          </button>
        )}
      </div>

      {/* ── Saved for You (LOCAL items) ── */}
      <div className="sidebar-card ls-info-section">
        <div className="ls-section-header">
          <span className="ls-section-bar" />
          <h4 className="ls-section-title">Saved for You</h4>
          <span className="ls-section-chevron">›</span>
          {canEdit && (
            <button
              className="add-info-btn"
              onClick={() => handleCreateInfo('LOCAL')}
              aria-label="Add item"
            />
          )}
        </div>
        <div className="ls-info-list">
          {localItems.length === 0 ? (
            <p className="no-items">No items available</p>
          ) : (
            localItems.map((item) => (
              <div key={item.id} className="ls-info-item">
                <a
                  href={item.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ls-info-link"
                >
                  <span className="ls-doc-icon" aria-hidden>
                    <svg width="13" height="15" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M8 1H2C1.46957 1 0.960859 1.21071 0.585786 1.58579C0.210714 1.96086 0 2.46957 0 3V13C0 13.5304 0.210714 14.0391 0.585786 14.4142C0.960859 14.7893 1.46957 15 2 15H12C12.5304 15 13.0391 14.7893 13.4142 14.4142C13.7893 14.0391 14 13.5304 14 13V7L8 1Z"
                        fill="#1085cb"
                        fillOpacity="0.12"
                        stroke="#1085cb"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path d="M8 1V7H14" stroke="#1085cb" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 10H11" stroke="#1085cb" strokeWidth="1.2" strokeLinecap="round"/>
                      <path d="M3 12.5H8" stroke="#1085cb" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  </span>
                  <span className='ls-info-title'>{item.title}</span>
                  <span className="ls-eye-icon">👁️</span>
                </a>
                {canEdit && (
                  <div className="ls-info-actions">
                    <button className="ls-action-btn" onClick={() => handleEditInfo(item)} title="Edit">✏️</button>
                    <button className="ls-action-btn" onClick={() => handleDeleteInfo(item)} title="Delete">🗑️</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Be the Right ONE (GLOBAL items) ── */}
      <div className="sidebar-card ls-info-section">
        <div className="ls-section-header">
          <span className="ls-section-bar" />
          <h4 className="ls-section-title">Be the right ONE</h4>
          <span className="ls-section-chevron">›</span>
          {canEdit && (
            <button
              className="add-info-btn"
              onClick={() => handleCreateInfo('GLOBAL')}
              aria-label="Add item"
            />
          )}
        </div>
        <div className="ls-info-list">
          {globalItems.length === 0 ? (
            <p className="no-items">No items available</p>
          ) : (
            globalItems.map((item) => (
              <div key={item.id} className="ls-info-item">
                <a
                  href={item.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ls-info-link"
                >
                  <span className="ls-bullet-icon">■</span>
                  {item.title}
                </a>
                {canEdit && (
                  <div className="ls-info-actions">
                    <button className="ls-action-btn" onClick={() => handleEditInfo(item)} title="Edit">✏️</button>
                    <button className="ls-action-btn" onClick={() => handleDeleteInfo(item)} title="Delete">🗑️</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Upcoming Events ── */}
      <EventsList
        events={events}
        canEdit={canEdit}
        onCreate={handleCreateEvent}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
      />

      {/* ── Event Modal ── */}
      {showEventModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'Add Event'}</h2>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>×</button>
            </div>
            <form onSubmit={handleEventSubmit}>
              <div className="modal-body">
                {eventError && <div className="error-message">{eventError}</div>}
                <div className="form-group">
                  <label>Event Title *</label>
                  <input type="text" value={eventFormData.title} onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })} placeholder="e.g., Monthly Management Meeting" required />
                </div>
                <div className="form-group">
                  <label>Event Date *</label>
                  <input type="date" value={eventFormData.eventDate} onChange={(e) => setEventFormData({ ...eventFormData, eventDate: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Event Time</label>
                  <input type="time" value={eventFormData.eventTime} onChange={(e) => setEventFormData({ ...eventFormData, eventTime: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={eventFormData.description} onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })} placeholder="Optional event description" rows={3} />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input type="number" value={eventFormData.displayOrder} onChange={(e) => setEventFormData({ ...eventFormData, displayOrder: parseInt(e.target.value) || 0 })} min="0" />
                </div>
                <div className="form-group">
                  <label>Image (Optional)</label>
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button type="button" className="remove-image" onClick={removeImage}>&times;</button>
                      </div>
                    ) : (
                      <div className="upload-placeholder" onClick={() => fileInputRef.current?.click()}>
                        <span className="upload-icon">📷</span>
                        <span>Click to upload image</span>
                      </div>
                    )}
                    <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" style={{ display: 'none' }} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={shouldNotify} onChange={(e) => setShouldNotify(e.target.checked)} />
                    <span>Send notification to all users</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEventModal(false)} disabled={isSavingEvent}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={isSavingEvent}>{isSavingEvent ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Delete Event Modal ── */}
      {showDeleteEventModal && createPortal(
        <div className="modal-overlay" onClick={cancelDeleteEvent}>
          <div className="modal-content delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Event</h2>
              <button className="modal-close" onClick={cancelDeleteEvent}>×</button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-message">Are you sure you want to delete this event? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDeleteEvent} disabled={isDeleting}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDeleteEvent} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Info Item Modal ── */}
      {showInfoModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingInfo ? 'Edit Item' : 'Add Item'}</h2>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <form onSubmit={handleInfoSubmit}>
              <div className="modal-body">
                {infoError && <div className="error-message">{infoError}</div>}
                <div className="form-group">
                  <label>Category *</label>
                  <select value={infoFormData.category} onChange={(e) => setInfoFormData({ ...infoFormData, category: e.target.value as InfoCategory })} required disabled={!!editingInfo}>
                    <option value="GLOBAL">Be the Right ONE</option>
                    <option value="LOCAL">Saved for You</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Title *</label>
                  <input type="text" value={infoFormData.title} onChange={(e) => setInfoFormData({ ...infoFormData, title: e.target.value })} placeholder="e.g., Global Vision, Holiday Calendar" required />
                </div>
                <div className="form-group">
                  <label>Document {editingInfo ? '(optional)' : '*'}</label>
                  <input type="file" onChange={(e) => setInfoFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" required={!editingInfo} />
                  <span className="field-note">Supported: PDF, Word, Excel, PowerPoint</span>
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input type="number" value={infoFormData.displayOrder} onChange={(e) => setInfoFormData({ ...infoFormData, displayOrder: parseInt(e.target.value) || 0 })} min="0" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowInfoModal(false)} disabled={isUploadingInfo}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={isUploadingInfo}>{isUploadingInfo ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Kaizen modal ── */}
      {showKaizenModal &&
        createPortal(
          <div className="modal-overlay kaizen-modal-overlay" onClick={closeKaizenModal}>
            <div className="modal-content kaizen-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header kaizen-modal-header">
                <h2>改善 Kaizen box</h2>
                <button type="button" className="modal-close kaizen-modal-close" onClick={closeKaizenModal} aria-label="Close">
                  ×
                </button>
              </div>
              {kaizenSuccess ? (
                <div className="kaizen-success-body">
                  <div className="kaizen-success-icon">✓</div>
                  <h3 className="kaizen-success-title">Idea submitted!</h3>
                  <p className="kaizen-success-msg">
                    Thank you for sharing your improvement idea. Your suggestion has been recorded and will be reviewed by the team.
                  </p>
                  <div className="modal-footer kaizen-modal-footer">
                    <button type="button" className="btn-submit kaizen-btn-submit" onClick={closeKaizenModal}>
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleKaizenSubmit}>
                  <div className="modal-body kaizen-modal-body">
                    {kaizenError && <div className="error-message kaizen-modal-error">{kaizenError}</div>}
                    <p className="kaizen-modal-lead">
                      Small improvements matter. Describe your idea — optional title and area help route it.
                    </p>
                    <div className="form-group">
                      <label htmlFor="kaizen-title">Title (optional)</label>
                      <input
                        id="kaizen-title"
                        type="text"
                        value={kaizenForm.title}
                        onChange={(e) => setKaizenForm({ ...kaizenForm, title: e.target.value })}
                        placeholder="e.g. Faster parts handoff at service bay"
                        maxLength={300}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="kaizen-area">Area / process (optional)</label>
                      <input
                        id="kaizen-area"
                        type="text"
                        value={kaizenForm.area}
                        onChange={(e) => setKaizenForm({ ...kaizenForm, area: e.target.value })}
                        placeholder="e.g. Service, Parts, Showroom"
                        maxLength={200}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="kaizen-idea">Your idea *</label>
                      <textarea
                        id="kaizen-idea"
                        value={kaizenForm.idea}
                        onChange={(e) => setKaizenForm({ ...kaizenForm, idea: e.target.value })}
                        placeholder="What would you change, and what benefit do you expect?"
                        rows={6}
                        maxLength={8000}
                        required
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer kaizen-modal-footer">
                    <button type="button" className="btn-cancel kaizen-btn-cancel" onClick={closeKaizenModal} disabled={kaizenSubmitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-submit kaizen-btn-submit" disabled={kaizenSubmitting}>
                      {kaizenSubmitting ? 'Submitting…' : 'Submit idea'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* ── Kaizen ideas list (Admin / Co-Admin) ── */}
      {showKaizenListModal &&
        createPortal(
          <div className="modal-overlay kaizen-modal-overlay" onClick={closeKaizenListModal}>
            <div
              className="modal-content kaizen-modal kaizen-list-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="kaizen-list-heading"
            >
              <div className="modal-header kaizen-modal-header">
                <h2 id="kaizen-list-heading">Submitted Kaizen ideas</h2>
                <button
                  type="button"
                  className="modal-close kaizen-modal-close"
                  onClick={closeKaizenListModal}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body kaizen-modal-body kaizen-list-modal-body">
                {kaizenListLoading && <p className="kaizen-list-status">Loading…</p>}
                {kaizenListError && <div className="error-message kaizen-modal-error">{kaizenListError}</div>}
                {!kaizenListLoading && !kaizenListError && kaizenIdeas.length === 0 && (
                  <p className="kaizen-list-empty">No ideas submitted yet.</p>
                )}
                {!kaizenListLoading && kaizenIdeas.length > 0 && (
                  <ul className="kaizen-idea-list">
                    {kaizenIdeas.map((row) => (
                      <li key={row.id} className="kaizen-idea-card">
                        <div className="kaizen-idea-meta">
                          <time dateTime={row.createdAt}>{formatKaizenSubmittedAt(row.createdAt)}</time>
                          {row.submittedBy && (
                            <span className="kaizen-idea-author">
                              {row.submittedBy.fullName}
                              {row.submittedBy.branch ? ` · ${row.submittedBy.branch}` : ''}
                            </span>
                          )}
                        </div>
                        {row.title && <div className="kaizen-idea-title">{row.title}</div>}
                        {row.area && <div className="kaizen-idea-area">Area: {row.area}</div>}
                        <p className="kaizen-idea-text">{row.idea}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="modal-footer kaizen-modal-footer">
                <button type="button" className="btn-submit kaizen-btn-submit" onClick={closeKaizenListModal}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Delete Info Modal ── */}
      {showDeleteInfoModal && createPortal(
        <div className="modal-overlay" onClick={cancelDeleteInfo}>
          <div className="modal-content delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Item</h2>
              <button className="modal-close" onClick={cancelDeleteInfo}>×</button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-message">
                Are you sure you want to delete <strong>"{infoToDelete?.title}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDeleteInfo} disabled={isDeletingInfo}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDeleteInfo} disabled={isDeletingInfo}>{isDeletingInfo ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </aside>
  );
};

export default LeftSidebar;
